import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import moment from 'moment';
import {
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContactChatListItem from 'src/views/management/ChatView/ChatSidebar/styled/ContactChatListItem';
import PhonesList from 'src/views/management/ChatView/ChatSidebar/PhonesList';
import { getChatsList, markChatAsUnread, updateChatPreferences } from 'src/actions/chatActions';
import helpers from 'src/helpers';
import { resolveTransportPendingState } from 'src/helpers/messageTransportPending';
import { ROUTES } from 'src/constants';

function contactSelector(state, currentChat) {
  const { chatsContacts } = state?.chat || {};
  const contactId = currentChat?.participantIds?.[0];
  if (!contactId) return undefined;

  // Object keys are always strings, so convert contactId to string for lookup
  const contact = chatsContacts.byId[String(contactId)];
  return contact;
}

export const getLastMessageInfo = (lastMessage) => {
  if (!lastMessage) return '';

  const trimmedLastMessageBody = lastMessage?.body?.trim() || '';
  const hasAttachment = !helpers.isEmpty(lastMessage?.attachments);
  const isMessageDeleted = lastMessage?.deletedAt !== null;
  const isEmptyMessage = helpers.isEmpty(trimmedLastMessageBody);
  if (isMessageDeleted) {
    return 'Message Deleted';
  }

  if (isEmptyMessage && hasAttachment) {
    const firstAttachment = lastMessage.attachments.find((attachment) => !!attachment);
    if (firstAttachment) {
      const fileExtension = firstAttachment.toLowerCase().split('.').pop();
      const isImage = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(fileExtension);
      const isVideo = ['mp4', 'webm', 'mov', 'avi', 'mkv', '3gp', 'flv', 'wmv'].includes(fileExtension);
      const isAudio = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'amr', 'wma', 'm4a'].includes(fileExtension);
      const isContact = ['vcf'].includes(fileExtension);
      const isArchive = ['zip', 'rar', '7z'].includes(fileExtension);
      const isDocument = ['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(fileExtension);
      if (isDocument) return 'Sent document';
      if (isArchive) return 'Sent archive';
      if (isContact) return 'Sent contact';
      if (isVideo) return 'Sent video';
      if (isAudio) return 'Sent audio';
      if (isImage) return 'Sent a photo';
    }
    return 'Sent a photo';
  }

  if (isEmptyMessage && !hasAttachment) {
    return <em>Empty Message</em>;
  }

  return lastMessage?.body || '';
};

export const getLastMessageTime = (timestamp) => {
  const now = moment();
  const lastMessage = moment(timestamp);

  const diffInHours = now.diff(lastMessage, 'hours');
  const diffInDays = now.diff(lastMessage, 'days');

  if (diffInHours < 2) {
    return lastMessage.format('h:mm a');
  }

  if (diffInDays < 1) {
    return `${diffInHours}h`;
  }

  if (diffInDays < 7) {
    return `${diffInDays}d`;
  }

  if (diffInDays < 365) {
    return lastMessage.format('MMM D');
  }

  return lastMessage.format('MMM D YY');
};

const getEarliestScheduledTime = (scheduledMessages) => {
  if (helpers.isEmpty(scheduledMessages)) return null;

  const earliest = scheduledMessages.reduce((min, msg) => {
    if (!msg?.scheduledAt) return min;
    const msgTime = moment(msg.scheduledAt);
    return !min || msgTime.isBefore(min) ? msgTime : min;
  }, null);

  if (!earliest) return null;

  const now = moment();
  if (earliest.isSame(now, 'day')) {
    return `Today, ${earliest.format('h:mm A')}`;
  }
  if (earliest.isSame(now.clone().add(1, 'day'), 'day')) {
    return `Tomorrow, ${earliest.format('h:mm A')}`;
  }
  return earliest.format('MMM D, h:mm A');
};

function ChatItem({
  chat = {},
  onClick,
  filterOptions = {},
}) {
  const [contextMenu, setContextMenu] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const contact = useSelector((state) => contactSelector(state, chat));
  const {
    key,
    messages,
    scheduledMessages,
    unreadCount,
  } = chat || {};
  const {
    isPinned = false,
    flagColor = 'none',
    isArchived = false,
  } = contact?.chatPreferences || {};
  const chatUnreadCount = useMemo(() => (unreadCount > 0 ? unreadCount : 0), [unreadCount]);
  const lastMessage = messages?.at(-1);
  const isChatActive = location.pathname === `${ROUTES.APP_MANAGEMENT_CHAT}/${key}`;
  const statusObj = helpers.transform.jsonToObject(lastMessage?.status);
  // A transport-unconfirmed message shows NO error flag in the sidebar while
  // it is still pending — the send most likely succeeded. It only earns the
  // error icon once the pending window elapses without confirmation.
  const transportPendingState = resolveTransportPendingState(lastMessage, statusObj);
  const isSendingError = !!statusObj?.error || transportPendingState.isExpired;

  const contactPhones = helpers.transform
    .toArray([contact?.phone1, contact?.phone2, contact?.phone3,])
    .filter((phone) => !helpers.isEmpty(phone));

  // Build parallel array of phone info for each phone number
  const contactPhoneInfoList = [
    contact?.phone1Info,
    contact?.phone2Info,
    contact?.phone3Info,
  ].filter((_, index) => !helpers.isEmpty([contact?.phone1, contact?.phone2, contact?.phone3][index]));

  const goTo = useCallback((url) => history.push(url), [history]);

  const handleContextMenu = (event) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
        }
        : null,
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleKebabClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setContextMenu({
      mouseX: rect.right,
      mouseY: rect.bottom,
    });
  };

  const handleMarkChatAsUnread = () => {
    const doWhenTry = () => {
      if (isChatActive) {
        goTo(ROUTES.APP_MANAGEMENT_CHAT);
      }
    };
    dispatch(markChatAsUnread(key, doWhenTry()));
    handleClose();
  };

  const handleTogglePin = () => {
    dispatch(updateChatPreferences(
      key,
      { isPinned: !isPinned },
      () => {
        // Refresh the chat list to re-sort with new pin status, maintaining current filter
        dispatch(getChatsList(true, '', filterOptions));
        // Scroll to top after list refreshes so user sees the pinned contact
        setTimeout(() => {
          const scrollBox = document.getElementById('scrollBox');
          if (scrollBox) {
            scrollBox.scrollTop = 0;
          }
        }, 100);
      }
    ));
    handleClose();
  };

  const handleSetFlag = (color) => {
    dispatch(updateChatPreferences(key, { flagColor: color }));
    handleClose();
  };

  const handleToggleArchive = () => {
    dispatch(updateChatPreferences(
      key,
      { isArchived: !isArchived },
      () => {
        // Refresh the chat list to remove/restore archived contact, maintaining current filter
        dispatch(getChatsList(true, '', filterOptions));
      }
    ));
    if (!isArchived && isChatActive) {
      goTo(ROUTES.APP_MANAGEMENT_CHAT);
    }
    handleClose();
  };

  const flagColorMap = {
    red: '#F44336',
    orange: '#FF9800',
    yellow: '#FFEB3B',
    green: '#4CAF50',
    blue: '#2196F3',
  };

  return (
    <div
      data-chat-key={key}
      onContextMenu={(event) => {
        handleContextMenu(event);
      }}
      style={{ cursor: 'pointer' }}
    >
      <ContactChatListItem
        component={RouterLink}
        to={`${ROUTES.APP_MANAGEMENT_CHAT}/${key}`}
        active={isChatActive}
        onClick={onClick}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
            marginBottom: '6px',
          }}
        >
          <Box sx={{
            display: 'flex', alignItems: 'center', overflow: 'hidden', gap: '4px',
          }}
          >
            {isPinned && (
              <PushPinIcon
                sx={{
                  fontSize: '16px',
                  color: '#1976d2',
                  flexShrink: 0,
                }}
              />
            )}
            {flagColor && flagColor !== 'none' && (
              <FlagIcon
                sx={{
                  fontSize: '16px',
                  color: flagColorMap[flagColor] || '#9e9e9e',
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              variant="h5"
              color="textPrimary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {helpers.transform.arrayToStringWithoutEmpty([contact?.firstName, contact?.lastName], ' ')}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ flexShrink: 0 }}
          >
            {!helpers.isEmpty(lastMessage) ? getLastMessageTime(lastMessage?.scheduledAt || lastMessage?.receivedAt) : ''}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {!helpers.isEmpty(contactPhones) ? (
            <PhonesList phoneNumbers={contactPhones} phoneInfoList={contactPhoneInfoList} />
          ) : (
            <Box />
          )}
          <IconButton
            size="small"
            onClick={handleKebabClick}
            sx={{
              padding: '2px',
              color: '#9e9e9e',
              flexShrink: 0,
              '&:hover': {
                color: '#616161',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <MoreVertIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            minHeight: chatUnreadCount ? '20px' : 0,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            {isSendingError && (
              <ErrorIcon
                sx={{
                  marginRight: '4px',
                  fontSize: '14px',
                  color: '#F15642',
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                textOverflow: 'ellipsis',
                color: '#546E7A',
                paddingRight: '4px',
              }}
              noWrap
            >
              {getLastMessageInfo(lastMessage)}
            </Typography>
          </Box>
          <Badge
            badgeContent={chatUnreadCount}
            invisible={!chatUnreadCount}
            color="secondary"
            sx={{
              marginLeft: '30px',
              '& .MuiBadge-badge': {
                right: 10,
                top: 10,
              }
            }}
          />
        </Box>
        {!helpers.isEmpty(scheduledMessages) && (() => {
          const earliestTime = getEarliestScheduledTime(scheduledMessages);
          return (
            <Box
              sx={{
                display: 'flex',
                gap: '4px',
                fontStyle: 'italic',
                color: '#8D8D8D',
              }}
            >
              <ScheduleSendIcon fontSize="small" />
              <Typography
                variant="caption"
                sx={{
                  lineHeight: '24px',
                }}
              >
                {earliestTime ? `Scheduled · ${earliestTime}` : 'Scheduled'}
              </Typography>
            </Box>
          );
        })()}
      </ContactChatListItem>
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleTogglePin}>
          {isPinned ? (
            <>
              <PushPinOutlinedIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
              Unpin
            </>
          ) : (
            <>
              <PushPinIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
              Pin to top
            </>
          )}
        </MenuItem>
        <MenuItem
          disabled={chatUnreadCount > 0}
          onClick={handleMarkChatAsUnread}
        >
          <MarkAsUnreadIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
          Mark as unread
        </MenuItem>
        <Box sx={{ padding: '6px 16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            {flagColor !== 'none' ? (
              <FlagIcon sx={{ marginRight: '8px', fontSize: '18px', color: flagColorMap[flagColor] }} />
            ) : (
              <FlagOutlinedIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
            )}
            <Typography variant="body1">Flag</Typography>
          </Box>
          <Box sx={{
            display: 'flex', justifyContent: 'flex-start', gap: '8px',
          }}
          >
            {Object.entries(flagColorMap).map(([color, hex]) => (
              <Box
                key={color}
                onClick={() => handleSetFlag(color)}
                sx={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: hex,
                  cursor: 'pointer',
                  border: flagColor === color ? '2px solid #333' : '2px solid transparent',
                  '&:hover': { opacity: 0.8 },
                }}
              />
            ))}
            {flagColor !== 'none' && (
              <Box
                onClick={() => handleSetFlag('none')}
                sx={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#e0e0e0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: '#666',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                ✕
              </Box>
            )}
          </Box>
        </Box>
        <MenuItem onClick={handleToggleArchive}>
          {isArchived ? (
            <>
              <UnarchiveIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
              Unarchive Chat
            </>
          ) : (
            <>
              <ArchiveIcon sx={{ marginRight: '8px', fontSize: '18px' }} />
              Archive Chat
            </>
          )}
        </MenuItem>
      </Menu>
    </div>
  );
}

ChatItem.propTypes = {
  onClick: PropTypes.func.isRequired,
  chat: PropTypes.object,
  filterOptions: PropTypes.object,
};

export default ChatItem;
